import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  useTheme,
  Divider,
  Link,
  Stack,
  IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

const Home = () => {
  const theme = useTheme();
  const calendlyLink = 'https://calendly.com/fambercontact/30min';

  return (
    <Box sx={{ backgroundColor: '#000000' }}>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg,rgb(0, 0, 0) 0%,rgb(1, 1, 1) 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.15) 0%, rgba(0, 0, 0, 0) 50%)',
            zIndex: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("/wave-pattern.svg")',
            opacity: 0.2,
            zIndex: 1,
            animation: 'wave 20s -3s linear infinite',
            '@keyframes wave': {
              '0%': {
                backgroundPosition: '0 0',
                transform: 'scale(1)',
              },
              '50%': {
                transform: 'scale(1.05)',
              },
              '100%': {
                backgroundPosition: '100% 100%',
                transform: 'scale(1)',
              },
            },
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography
            variant="h1"
            sx={{
              mb: 3,
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 700,
              background: 'linear-gradient(135deg, #8B5CF6 0%, #4ADE80 50%, #3B82F6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'center',
              textShadow: '0 0 40px rgba(139, 92, 246, 0.3)',
            }}
          >
            Faites évoluer votre entreprise avec une équipe d'agents IA.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mb: 6,
              fontSize: { xs: '1.5rem', md: '2.5rem' },
              color: '#ffffff',
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Famber.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/agents"
              sx={{
                backgroundColor: '#8B5CF6',
                color: 'white',
                px: 4,
                py: 2,
                '&:hover': {
                  backgroundColor: '#7C3AED',
                },
              }}
            >
              Découvrir nos agents
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/case-studies"
              sx={{
                borderColor: '#4ADE80',
                color: '#4ADE80',
                px: 4,
                py: 2,
                '&:hover': {
                  borderColor: '#3B82F6',
                  backgroundColor: 'rgba(74, 222, 128, 0.1)',
                },
              }}
            >
              En savoir plus
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Information Blocks */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Grid container spacing={4}>
          {[
            {
              title: "LA MARKETPLACE D'AGENTS IA",
              description: "Famber est la plateforme nouvelle génération où les développeurs peuvent héberger, partager et monétiser les meilleurs agents IA du monde.",
            },
            {
              title: "GUIDER L'IA VERS L'EXCELLENCE",
              description: "Accédez instantanément à un catalogue de plus de 1 000 agents IA prêts à l'emploi, sans configuration complexe.",
            },
            {
              title: "L'AVENIR DE L'AUTOMATISATION",
              description: "Fondée en 2024, Famber révolutionne l'automatisation des tâches grâce à nos partenariats avec les leaders de l'IA.",
            },
          ].map((block, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                  },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    color: '#8B5CF6',
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  {block.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#ffffff',
                    opacity: 0.8,
                  }}
                >
                  {block.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Agents IA Section */}
      <Box sx={{ py: 12, backgroundColor: '#0a0a0a' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              color: '#ffffff',
              mb: 8,
              fontWeight: 600,
            }}
          >
            Des agents IA qui redéfinissent le possible
          </Typography>
          
          <Grid container spacing={6}>
            {[
              {
                title: "Agents d'Analyse de Données",
                description: "Transformez des données brutes en insights stratégiques. Nos agents d'analyse peuvent traiter, visualiser et interpréter des ensembles de données complexes en quelques secondes.",
                color: "#8B5CF6"
              },
              {
                title: "Assistants de Rédaction",
                description: "Des agents spécialisés dans la création de contenu de qualité professionnelle, du copywriting marketing aux rapports techniques en passant par les descriptions de produits.",
                color: "#4ADE80"
              },
              {
                title: "Agents de Service Client",
                description: "Offrez un support client 24/7 sans compromis. Nos agents répondent aux questions, résolvent les problèmes et transmettent les cas complexes quand nécessaire.",
                color: "#3B82F6"
              },
              {
                title: "Chercheurs Virtuels",
                description: "Confiez vos recherches à nos agents capables de compiler, synthétiser et présenter des informations pertinentes sur n'importe quel sujet spécialisé.",
                color: "#EC4899"
              },
              {
                title: "Programmeurs IA",
                description: "Des agents capables de générer du code, déboguer des applications et optimiser des algorithmes dans une variété de langages de programmation.",
                color: "#F59E0B"
              },
              {
                title: "Agents de Productivité",
                description: "Automatisez vos tâches répétitives, organisez votre agenda et optimisez vos processus de travail grâce à des agents personnalisés à vos besoins.",
                color: "#10B981"
              },
            ].map((agent, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderTop: `4px solid ${agent.color}`,
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 10px 30px rgba(0, 0, 0, 0.5), 0 0 0 1px ${agent.color}33`,
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      color: agent.color,
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {agent.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#ffffff',
                      opacity: 0.9,
                      mb: 3,
                    }}
                  >
                    {agent.description}
                  </Typography>
                  <Button
                    variant="text"
                    component={RouterLink}
                    to="/agents"
                    sx={{
                      color: agent.color,
                      p: 0,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Explorer →
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Plateforme Marketplace Section */}
      <Box sx={{ py: 12, backgroundColor: '#101010' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: '#1a1a1a',
                  borderRadius: '16px',
                  p: 2,
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  height: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at top right, rgba(139, 92, 246, 0.2) 0%, rgba(0, 0, 0, 0) 70%)',
                    zIndex: 1,
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(74, 222, 128, 0.1) 100%)',
                    zIndex: 1,
                  },
                  boxShadow: '0 0 30px rgba(139, 92, 246, 0.2)',
                }}
              >
                <Box
                  component="img"
                  src="/marketplace-illustration.jpg"
                  alt="Illustration de la marketplace"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'relative',
                    zIndex: 2,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  color: '#ffffff',
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                La nouvelle référence pour les agents IA
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#ffffff',
                  opacity: 0.8,
                  mb: 4,
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                }}
              >
                Famber redéfinit le marché des agents IA en créant un écosystème où développeurs et utilisateurs se rencontrent dans un environnement sécurisé et innovant. Notre marketplace permet aux créateurs de monétiser leur expertise en IA et aux entreprises d'accéder aux meilleurs agents sans développement interne coûteux.
              </Typography>
              <Grid container spacing={3}>
                {[
                  { stat: "10+", label: "Agents bientôt disponibles" },
                  { stat: "100+", label: "Utilisateurs actifs" },
                  { stat: "50+", label: "Développeurs certifiés" },
                  { stat: "98%", label: "Taux de satisfaction" }
                ].map((item, index) => (
                  <Grid item xs={6} key={index}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: '#8B5CF6',
                        fontWeight: 700,
                      }}
                    >
                      {item.stat}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#ffffff',
                        opacity: 0.7,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Button
                component="a"
                href="https://tally.so/r/mJWEgJ"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  mt: 5,
                  backgroundColor: '#8B5CF6',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#7C3AED',
                  },
                }}
              >
                Rejoindre la plateforme
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pour les développeurs */}
      <Box sx={{ py: 12, backgroundColor: '#0a0a0a' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              color: '#ffffff',
              mb: 6,
              fontWeight: 600,
            }}
          >
            Développeurs, monétisez votre expertise en IA
          </Typography>
          
          <Grid container spacing={4}>
            {[
              {
                title: "Déploiement simplifié",
                description: "Notre infrastructure permet un déploiement facile de vos agents sans gestion complexe des serveurs ou de l'infrastructure.",
              },
              {
                title: "Audience mondiale",
                description: "Accédez instantanément à une clientèle mondiale à la recherche des meilleurs agents IA pour leurs besoins spécifiques.",
              },
              {
                title: "Monétisation flexible",
                description: "Choisissez votre modèle de revenus : abonnements, paiement à l'utilisation ou licences entreprise.",
              },
              {
                title: "Outils d'analyse",
                description: "Suivez les performances de vos agents et obtenez des insights précieux sur leur utilisation pour les améliorer continuellement.",
              },
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: '#4ADE80',
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#ffffff',
                      opacity: 0.8,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              component="a"
              href="https://calendly.com/fambercontact/30min"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderColor: '#4ADE80',
                color: '#4ADE80',
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: '#3dcb71',
                  backgroundColor: 'rgba(74, 222, 128, 0.1)',
                },
              }}
            >
              Devenir développeur Famber
            </Button>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 12, backgroundColor: '#0a0a0a' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundColor: '#141414',
              borderRadius: '24px',
              p: 6,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '120%',
                height: '120%',
                background: 'radial-gradient(circle at center, rgba(139, 92, 246, 0.15) 0%, rgba(0, 0, 0, 0) 70%)',
                zIndex: 0,
              },
            }}
          >
            <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="h2"
                  sx={{
                    color: '#ffffff',
                    fontWeight: 700,
                    mb: 4,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                  }}
                >
                  Questions Fréquentes
                </Typography>
                <Button
                  variant="contained"
                  component="a"
                  href="https://tally.so/r/mJWEgJ"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: '#4F46E5',
                    color: '#ffffff',
                    px: 4,
                    py: 1.5,
                    borderRadius: '50px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': {
                      backgroundColor: '#4338CA',
                    },
                  }}
                >
                  Essai gratuit →
                </Button>

                <Box
                  sx={{
                    mt: 8,
                    p: 4,
                    backgroundColor: '#1A1A1A',
                    borderRadius: '16px',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'radial-gradient(circle at top right, rgba(139, 92, 246, 0.1) 0%, rgba(0, 0, 0, 0) 70%)',
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ color: '#ffffff', mb: 2, position: 'relative' }}>
                    Vous avez une question ?
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3, position: 'relative' }}
                  >
                    Nous serons ravis de vous aider.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Box
                        key={i}
                        component="img"
                        src={`/team-member-${i}.jpg`}
                        alt={`Support team member ${i}`}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          border: '2px solid #141414',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    {
                      question: "À quoi sert Famber ?",
                      answer: `Famber est la plateforme tout-en-un pour automatiser vos tâches avec des agents IA prêts à l'emploi.

                  ✅ Trouver l'agent IA parfait parmi un catalogue en pleine expansion
                  ✅ Automatiser vos processus sans effort et sans code
                  ✅ Personnaliser vos agents pour répondre à vos besoins précis
                  ✅ Intégrer l'IA à vos outils en quelques minutes
                  ✅ Sécuriser vos données avec les meilleurs standards du marché`
                    },
                    {
                      question: "Comment tester un agent IA ?",
                      answer: `Lancez-vous en 3 étapes simples :
                  
                  1️⃣ Explorez notre catalogue et sélectionnez l'agent adapté à votre besoin
                  2️⃣ Activez votre essai gratuit en un clic, sans engagement
                  3️⃣ Testez toutes les fonctionnalités et intégrez l'agent à vos outils en quelques minutes`
                    },
                    {
                      question: "Quelles fonctionnalités sont disponibles pendant l'essai gratuit ?",
                      answer: `Dès votre inscription, vous accédez à 100% des fonctionnalités :
                  
                  ✔️ Agents IA sans aucune limitation
                  ✔️ Intégrations avec plus de 100 outils
                  ✔️ Tableau de bord avancé pour suivre les performances
                  ✔️ Personnalisation complète des paramètres de l'agent
                  ✔️ Support technique dédié pour vous accompagner`
                    },
                    {
                      question: "Comment intégrer un agent avec mes outils ?",
                      answer: `Famber s'adapte à votre écosystème grâce à plusieurs solutions d'intégration :
                  
                 →  Connecteurs natifs avec les outils les plus populaires (Slack, Gmail, Notion…)
                 → API REST complète pour une intégration personnalisée
                 → Webhooks & automatisations avancées
                 →  No-code & low-code : déployez un agent sans écrire une ligne de code`
                    },
                    {
                      question: "Quel support technique est disponible ?",
                      answer: `Notre support technique vous accompagne à chaque étape avec un service réactif par chat et email, une base de connaissances complète et des guides détaillés. Vous pouvez également profiter de webinaires et de formations gratuites pour approfondir vos compétences, ainsi que d'une communauté d'utilisateurs engagés pour échanger et partager des conseils. 
                      
                      Besoin d'aide ? Notre équipe est à votre disposition !`
                      
                    },
                    {
                      question: "Comment garantissez-vous la sécurité et la confidentialité ?",
                      answer: `La sécurité est une priorité chez Famber :
                  
                  🔒 Chiffrement des données de bout en bout
                  🔒  Conformité RGPD et normes de cybersécurité
                  🔒 Authentification sécurisée & contrôle des accès
                  🔒️ Environnements isolés pour chaque agent`
                    }
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: '#1A1A1A',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        border: '1px solid rgba(139, 92, 246, 0.1)',
                        cursor: 'pointer', // Curseur pointer sur tout le bloc
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'radial-gradient(circle at right, rgba(139, 92, 246, 0.15) 0%, rgba(0, 0, 0, 0) 70%)',
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                        },
                        '&:hover': {
                          backgroundColor: '#242424',
                          borderColor: 'rgba(139, 92, 246, 0.3)',
                          boxShadow: '0 0 20px rgba(139, 92, 246, 0.2)',
                          '&::before': {
                            opacity: 1,
                          },
                        },
                      }}
                      onClick={() => { // Gestionnaire d'événement au niveau du conteneur principal
                        const el = document.getElementById(`faq-answer-${index}`);
                        const arrow = document.getElementById(`faq-arrow-${index}`);
                        if (el && arrow) {
                          const isHidden = el.style.maxHeight === '0px' || el.style.maxHeight === '';
                          el.style.maxHeight = isHidden ? `${el.scrollHeight}px` : '0px';
                          el.style.opacity = isHidden ? '1' : '0';
                          arrow.style.transform = isHidden ? 'rotate(180deg)' : 'rotate(0deg)';
                        }
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 2.5, // Réduit de p: 3 à p: 2.5 pour diminuer la hauteur
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: '#ffffff',
                            fontWeight: 500,
                            fontSize: '1.1rem',
                          }}
                        >
                          {item.question}
                        </Typography>
                        <Box
                          id={`faq-arrow-${index}`}
                          sx={{
                            color: '#ffffff',
                            transition: 'transform 0.3s ease',
                            transform: 'rotate(0deg)',
                          }}
                        >
                          ▼
                        </Box>
                      </Box>
                      <Box
                        id={`faq-answer-${index}`}
                        sx={{
                          maxHeight: '0px',
                          opacity: 0,
                          overflow: 'hidden',
                          transition: 'all 0.3s ease-in-out',
                          px: 3,
                          pb: 3,
                          whiteSpace: 'pre-line',
                          backgroundColor: 'rgba(139, 92, 246, 0.05)',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            lineHeight: 1.8,
                          }}
                        >
                          {item.answer}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box
        sx={{
          py: 16,
          background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)',
          position: 'relative',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
              }}
            >
              Prêt à révolutionner votre travail avec l'IA ?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#ffffff',
                opacity: 0.8,
                mb: 6,
                fontWeight: 400,
              }}
            >
              Rejoignez des milliers d'entreprises qui transforment leur productivité grâce à Famber.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Button
                variant="contained"
                size="large"
                component="a"
                href="https://tally.so/r/mJWEgJ"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: '#8B5CF6',
                  color: 'white',
                  px: 4,
                  py: 2,
                  '&:hover': {
                    backgroundColor: '#7C3AED',
                  },
                }}
              >
                Essai gratuit
              </Button>
              <Button
                variant="outlined"
                size="large"
                component="a"
                href="https://calendly.com/fambercontact/30min"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderColor: '#ffffff',
                  color: '#ffffff',
                  px: 4,
                  py: 2,
                  '&:hover': {
                    borderColor: '#ffffff',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Demander une démo
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;